// RecruitBoardEdit.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useState } from "react";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import BTEditor from "../../common/components/board/BTEditor";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import { dialogStore } from "../../store/DialogStore";
import {
  requestDeleteFile,
  requestUpdateRecruitDocuments,
  requestUploadFiles
} from "../../service/BoardService";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";
import { formatDateToYMD } from "../../common/utils/DateUtils";

// 날짜 문자열을 UTC+9 오전 9시로 변환하는 함수
const createDateAt9AM = (dateString: string): Date => {
  if (!dateString || dateString.trim() === '') {
    throw new Error('Invalid date string: empty or null');
  }
  
  // UTC+9 시간대에서 오전 9시로 설정
  const date = new Date(dateString + 'T09:00:00+09:00');
  
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date string: ${dateString}`);
  }
  
  return date;
};

const RecruitBoardEdit = () => (
  <MainLayout>
    <RecruitBoardEditBody />
  </MainLayout>
);

export default RecruitBoardEdit;

const RecruitBoardEditBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const recruit = location.state?.recruit;
  const recruitTypeOptions = location.state?.recruitTypeOptions || [];

  const [jobTitle, setJobTitle] = useState(recruit?.jobTitle || "");
  const [contents, setContents] = useState(recruit?.contents || "");
  const [selectedImage, setSelectedImage] = useState<string | null>(recruit?.representImage || null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>(recruit?.attachedFiles || []);

  const [jobType, setJobType] = useState(recruit?.jobType || "");
  const [recruitStartDate, setRecruitStartDate] = useState(recruit?.recruitStartDate ? formatDateToYMD(recruit.recruitStartDate) : "");
  const [recruitEndDate, setRecruitEndDate] = useState(recruit?.recruitEndDate ? formatDateToYMD(recruit.recruitEndDate) : "");
  const [isPublished, setIsPublished] = useState(recruit?.isPublished ?? true);

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      // 본문 이미지 업로드/치환
      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, "text/html");
      const imgElements = docParsed.querySelectorAll("img");

      const imagesToUpload: File[] = [];
      const base64ImageSrcs: string[] = [];
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) base64ImageSrcs.push(src);
      });

      for (const base64Src of base64ImageSrcs) {
        const res = await fetch(base64Src);
        const blob = await res.blob();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.png`;
        imagesToUpload.push(new File([blob], fileName, { type: blob.type }));
      }

      const uploadedImageUrls =
        imagesToUpload.length > 0
          ? await requestUploadFiles("recruit", recruit.id, "image", imagesToUpload)
          : [];

      let idx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) {
          const newUrl = uploadedImageUrls[idx++];
          if (newUrl) img.setAttribute("src", newUrl);
          if (selectedImage === src) setSelectedImage(newUrl);
        }
      });

      // 삭제된 이미지 정리
      const prevDocParsed = recruit.contents
        ? parser.parseFromString(recruit.contents, "text/html")
        : null;
      const prevImgHttpSrcs = prevDocParsed
        ? Array.from(prevDocParsed.querySelectorAll("img"))
            .map((i) => i.getAttribute("src"))
            .filter((s): s is string => !!s && s.startsWith("http"))
        : [];
      const currImgHttpSrcs = Array.from(docParsed.querySelectorAll("img"))
        .map((i) => i.getAttribute("src"))
        .filter((s): s is string => !!s && s.startsWith("http"));
      const deletedImgUrls = prevImgHttpSrcs.filter((u) => !currImgHttpSrcs.includes(u));
      await Promise.all(
        deletedImgUrls.map(async (url) => {
          try {
            const match = url.match(/\/o\/([^?]+)\?/);
            if (match) {
              const path = decodeURIComponent(match[1]);
              await requestDeleteFile(path);
            }
          } catch {}
        })
      );

      // 첨부파일 삭제/유지/추가 반영
      const prevAttached: string[] = Array.isArray(recruit.attachedFiles) ? recruit.attachedFiles : [];
      const keepSet = new Set(uploadedFileUrls);
      const deletedFiles = prevAttached.filter((u) => !keepSet.has(u));
      await Promise.all(
        deletedFiles.map(async (url) => {
          try {
            const match = url.match(/\/o\/([^?]+)\?/);
            if (match) {
              const path = decodeURIComponent(match[1]);
              await requestDeleteFile(path);
            }
          } catch {}
        })
      );
      const newAttachmentUrls =
        uploadedFiles.length > 0
          ? await requestUploadFiles("recruit", recruit.id, "attachments", uploadedFiles)
          : [];
      const finalAttachedFiles = [
        ...uploadedFileUrls.filter((u) => prevAttached.includes(u)),
        ...newAttachmentUrls,
      ];

      const imgElements2 = docParsed.querySelectorAll("img");
      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });

      // 최종 데이터 반영 (누락 필드 포함)
      recruit.jobTitle = jobTitle;
      recruit.contents = docParsed.body.innerHTML;
      recruit.jobType = jobType;
      
      // 날짜 변환 (에러 처리 포함)
      if (!recruitStartDate || recruitStartDate.trim() === '') {
        throw new Error('채용 시작일을 입력해주세요.');
      }
      recruit.recruitStartDate = createDateAt9AM(recruitStartDate);
      
      if (recruitEndDate && recruitEndDate.trim() !== '') {
        recruit.recruitEndDate = createDateAt9AM(recruitEndDate);
      } else {
        recruit.recruitEndDate = null;
      }
      
      recruit.isPublished = isPublished;
      recruit.attachedFiles = finalAttachedFiles;
      recruit.updatedBy = authStore.user?.username ?? "";
      recruit.updatedAt = new Date();

      await requestUpdateRecruitDocuments(recruit);

      dialogStore.showDialog({
        title: "성공",
        message: "채용 공고가 성공적으로 수정되었습니다.",
        onConfirm: () =>
          navigate("/board/recruit/detail", { state: { recruit, recruitTypeOptions } }),
        onClose: () =>
          navigate("/board/recruit/detail", { state: { recruit, recruitTypeOptions } }),
      });
    } catch (e) {
      dialogStore.showDialog({
        title: "오류",
        message: "채용 공고 수정 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>채용공고 수정</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션: 채용 공고 정보 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>채용 공고 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 채용 유형 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>채용 유형</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <BTSelectBox
              width="100%"
              options={recruitTypeOptions}
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
            />
          </div>
        </div>

        <BTBox height="16px" />

        {/* 2줄: 시작일자 | 종료일자 | 글 공개 여부 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>채용 시작 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={recruitStartDate} onChange={setRecruitStartDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap" }}>채용 종료 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={recruitEndDate} onChange={setRecruitEndDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        {/* 섹션: 컨텐츠 입력 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>채용 공고 컨텐츠 입력</TitleMediumBold>
        <BTBox height="16px" />

        <BTEditor
          title={jobTitle}
          contents={contents}
          selectedImage={selectedImage}
          uploadedFiles={uploadedFiles}
          uploadedFileUrls={uploadedFileUrls}
          setTitle={setJobTitle}
          setContents={setContents}
          setSelectedImage={setSelectedImage}
          setUploadedFiles={setUploadedFiles}
          setUploadedFileUrls={setUploadedFileUrls}
          onSave={handleSave}
          onCancel={() => navigate(-1)}
          attachmentAccept="all"
        />
      </VerticalLayout>
    </VerticalLayout>
  );
});